/**
 * "Install CrewVex" auto-open frequency — per DEVICE (localStorage, no PII,
 * intentionally NOT cleared at sign-out — see `clearPerUserDeviceState.js`,
 * which is for per-USER caches; this key identifies neither a user nor a
 * tenant).
 *
 * Rule (locked spec): auto-open when `!done && (autoShows === 0 ||
 * (autoShows === 1 && now - lastShownAt >= 14 days))`. So the prompt shows
 * once, then once more no sooner than 14 days later, then never again unless
 * the user never dismissed it with "Got it" (`done` stays false — a second
 * auto-show is still allowed at the 14-day mark even if the first one was
 * just closed with ✕/scrim/Escape).
 *
 * Every read/write is try/catch'd — a private-window / blocked-storage
 * browser must render the modal fine, just without persistence. Storage that
 * THROWS is treated as "not showable" (fail closed on the auto-open path,
 * not fail open) — `readInstallPromptState` returns `null` in that case, and
 * `shouldAutoShowInstallPrompt(null, …)` is false.
 */

export const INSTALL_PROMPT_STORAGE_KEY = 'cvx.installPrompt.v1';
export const INSTALL_PROMPT_RESHOW_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * @returns {{autoShows: number, lastShownAt: number|null, done: boolean}|null}
 *   `null` means storage is unavailable/threw — callers must treat that as
 *   "cannot determine, do not auto-show," never as "never shown before."
 */
export function readInstallPromptState() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(INSTALL_PROMPT_STORAGE_KEY);
    if (!raw) return { autoShows: 0, lastShownAt: null, done: false };
    const parsed = JSON.parse(raw);
    return {
      autoShows: typeof parsed?.autoShows === 'number' ? parsed.autoShows : 0,
      lastShownAt: typeof parsed?.lastShownAt === 'number' ? parsed.lastShownAt : null,
      done: parsed?.done === true,
    };
  } catch {
    return null;
  }
}

function writeInstallPromptState(state) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(INSTALL_PROMPT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable — the prompt still renders, it just won't persist
    // frequency/done across reloads for this device.
  }
}

/**
 * Pure predicate — no storage access, so it's trivial to unit test the
 * boundary math directly (14-day edge, 2-auto-show cap, done, null state).
 */
export function shouldAutoShowInstallPrompt(state, now = Date.now()) {
  if (!state || state.done) return false;
  if (state.autoShows === 0) return true;
  if (state.autoShows === 1) {
    return typeof state.lastShownAt === 'number' && (now - state.lastShownAt) >= INSTALL_PROMPT_RESHOW_MS;
  }
  return false;
}

/** Called the moment the modal actually starts an AUTO-open (not a menu tap). */
export function recordInstallPromptShown(now = Date.now()) {
  const prev = readInstallPromptState() || { autoShows: 0, lastShownAt: null, done: false };
  const next = { ...prev, autoShows: (prev.autoShows || 0) + 1, lastShownAt: now };
  writeInstallPromptState(next);
  return next;
}

/** "Got it", or a successful install (Android accept / `appinstalled`). */
export function markInstallPromptDone() {
  const prev = readInstallPromptState() || { autoShows: 0, lastShownAt: null, done: false };
  const next = { ...prev, done: true };
  writeInstallPromptState(next);
  return next;
}
